import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useLocale } from '../../../state/useLocale';

/**
 * The stage every warm-up figure is drawn on, and the few shapes they share.
 *
 * A figure is a claim about a mechanism, drawn once. The frame gives each one
 * the same fixed 320 × 150 viewBox so a card can scale it to whatever width it
 * has without any figure knowing, and it owns the two rules about motion:
 * nothing animates until the card is actually on screen, and the story runs
 * exactly once when it arrives. Stepping back to a card plays it again, which
 * is the whole reason someone steps back, and is what remounting on `run` is
 * for — a CSS animation only starts when its element does.
 *
 * The animations themselves live in styles/figures.css. Everything here is
 * drawn in its *settled* state and the keyframes only play the story into it,
 * so a figure that never animates is still the finished drawing.
 *
 * The vocabulary is the live graph's on purpose: a commit is a filled circle,
 * an unreachable commit is a dashed ring on the panel's own ground, a ref is a
 * rounded pill and the current branch's pill is the filled white one
 * (components/play/GitGraph.tsx, styles/play.css). A shape a learner meets in
 * a warm-up is the shape they meet in the graph a minute later.
 *
 * It owns the one thing a figure has to know about language, too. The drawing
 * is left to right in both of them, but the words on it are not, so `Label`
 * is where a translated label gets its own direction and its anchor flipped
 * while the geometry stays exactly where it was. A machine token is not a
 * `Label` — that is the whole distinction, and it is drawn here.
 */

/** The stage. Cards scale it; no figure ever draws outside it. */
export const VIEW_W = 320;
export const VIEW_H = 150;

/** One character of the 9.5px mono face figures.css sets on every label. */
export const CH = 5.7;

/**
 * One Arabic letter at the same size.
 *
 * In Arabic the mono stack is Plex Arabic ahead of Space Mono (locale.css),
 * and Plex Arabic is proportional — its letters average a little under three
 * quarters of Space Mono's cell. Nothing in a figure is measured except the
 * pill, so one average is enough; it is only ever asked how wide to draw a
 * border, and the label inside it is centred rather than aligned to an edge.
 */
const CH_AR = 4.2;

/**
 * Written as code points rather than as a character class, because the ranges
 * are easier to check against Unicode than a line of glyphs nobody can read
 * in a diff: Arabic, its two supplements, and the presentation forms a shaper
 * may hand back.
 */
function isArabic(code: number): boolean {
  return (
    (code >= 0x0600 && code <= 0x06ff) ||
    (code >= 0x0750 && code <= 0x077f) ||
    (code >= 0x08a0 && code <= 0x08ff) ||
    (code >= 0xfb50 && code <= 0xfdff) ||
    (code >= 0xfe70 && code <= 0xfeff)
  );
}

/** The marks and isolates `ltr()` wraps a machine token in. They draw nothing. */
function isInvisible(code: number): boolean {
  return code === 0x200e || code === 0x200f || (code >= 0x2066 && code <= 0x2069);
}

/** How wide a label renders, near enough to size a border to. */
function textWidth(text: string): number {
  let w = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (isInvisible(code)) continue;
    w += isArabic(code) ? CH_AR : CH;
  }
  return w;
}

/** Width of a ref pill around a label — the graph's formula, at this size. */
export const pillWidth = (text: string): number => Math.round(textWidth(text) + 14);

/** Height of a ref pill. Fixed, like the graph's. */
export const PILL_H = 15;

/**
 * Timing and geometry for one beat, handed to CSS as custom properties.
 *
 * `delay` is when the beat starts, in milliseconds after the card arrives;
 * `vars` carries whatever else the effect needs — how long it runs (`--t`),
 * where it travels from (`--dx`, `--dy`), how far a stroke draws (`--len`).
 * Custom properties are not part of CSSProperties, so the cast is the same one
 * GitGraph.tsx makes for the edge it is drawing in.
 */
export function fx(delay: number, vars?: Record<string, string | number>): CSSProperties {
  return { '--d': `${delay}ms`, ...vars } as CSSProperties;
}

/**
 * A run counter that ticks every time the figure's card becomes the one on
 * screen. Adjusting it during the render that noticed the change, rather than
 * in an effect, means the remount happens before anything paints: the story
 * never starts twice, and a card that arrives already active still plays.
 */
function useRun(active: boolean): number {
  const [run, setRun] = useState(0);
  const [wasActive, setWasActive] = useState(active);
  if (active !== wasActive) {
    setWasActive(active);
    if (active) setRun((n) => n + 1);
  }
  return run;
}

export interface FrameProps {
  /** The figure's name, which becomes `fig-<name>` for anything figure-specific. */
  name: string;
  /** True while this figure's card is the one on screen. */
  active: boolean;
  children: ReactNode;
}

export function Frame({ name, active, children }: FrameProps) {
  const run = useRun(active);
  return (
    <svg
      className={`fig fig-${name}`}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      // The card's prose says the same thing in words, so there is nothing
      // here for a screen reader to gain and a diagram's worth of labels to
      // lose it in.
      aria-hidden="true"
      focusable="false"
    >
      {/* `fig-stage`, not `stage`: play.css owns a bare `.stage` for the pane
          column, and an unscoped grid rule landing on this group is not worth
          the risk. The same reason figures.css is `.fig` and not `.figure`. */}
      <g key={run} className={active ? 'fig-stage is-live' : 'fig-stage'}>
        {children}
      </g>
    </svg>
  );
}

/* ── Words ───────────────────────────────────────────────────────────────── */

/**
 * Which physical edge of a label sits at its `x`.
 *
 * Physical, not logical, and that is the whole point of this type. A figure is
 * a drawing: the box is at x 118 in both languages, so the word naming it is
 * at x 160 in both languages. SVG's own `text-anchor` is logical — `start` is
 * the left edge in English and the right edge in Arabic — so a label that
 * wants to stay put has to say which side it means and let `Label` work out
 * the anchor.
 */
type At = 'left' | 'mid' | 'right';

const ANCHOR: Record<'ltr' | 'rtl', Record<At, 'start' | 'middle' | 'end'>> = {
  ltr: { left: 'start', mid: 'middle', right: 'end' },
  rtl: { left: 'end', mid: 'middle', right: 'start' },
};

export interface LabelProps {
  x: number;
  y: number;
  /** Default `mid`: a label centred on the thing it names grows both ways. */
  at?: At;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

/**
 * A prose label: a word from the catalogue, set in the reader's own direction.
 *
 * The drawing around it is not. `[dir='rtl'] .fig` holds the whole stage left
 * to right (styles/locale.css, docs/i18n.md §7) because a commit graph reads
 * left to right in every language and a mirrored one teaches the wrong
 * picture. But a *sentence* on that stage is still Arabic, and inside a
 * left-to-right box an Arabic phrase with a Latin token in it — `كل commit
 * يشير إلى أبيه` — comes out with the token at the wrong end. So each label
 * gets its own base direction while the geometry keeps its.
 *
 * Two smaller things ride along. `letter-spacing` is 0.01em on `.fig` and
 * Arabic is a joined script, so tracking is turned off for the labels that
 * are Arabic and left alone on the Latin tokens beside them. And the anchor
 * is translated from a physical edge, so nothing in a figure moves when the
 * page turns around.
 *
 * Anything the machine wrote — a hash, a branch, a command, a filename, a
 * stack frame — is *not* a label. Those stay plain `<text>` with the string
 * in the component, which is what leaves them in Latin and left to right.
 */
export function Label({ x, y, at = 'mid', className, style, children }: LabelProps) {
  const { dir } = useLocale();
  return (
    <text
      className={className}
      x={x}
      y={y}
      textAnchor={ANCHOR[dir][at]}
      style={dir === 'rtl' ? { direction: 'rtl', letterSpacing: 'normal', ...style } : style}
    >
      {children}
    </text>
  );
}

/* ── Shapes ──────────────────────────────────────────────────────────────── */

type NodeKind = 'ink' | 'dim' | 'signal' | 'ghost';

export interface NodeProps {
  x: number;
  y: number;
  r?: number;
  kind?: NodeKind;
  className?: string;
  style?: CSSProperties;
  /** The resting opacity, which is also where a `fx-dim` beat ends. */
  opacity?: number;
}

/** A commit. `ghost` is the unreachable drawing: still there, no longer spoken for. */
export function Node({ x, y, r = 6, kind = 'ink', className, style, opacity }: NodeProps) {
  return (
    <circle
      className={['node', `node-${kind}`, className].filter(Boolean).join(' ')}
      cx={x}
      cy={y}
      r={r}
      style={style}
      opacity={opacity}
    />
  );
}

type PillKind = 'ref' | 'head' | 'ghost';

export interface PillProps {
  /** Left edge and top edge, so a pill can be placed against a node by hand. */
  x: number;
  y: number;
  text: string;
  kind?: PillKind;
  className?: string;
  style?: CSSProperties;
}

/** A ref label. `head` is the filled white pill: the branch HEAD is on. */
export function Pill({ x, y, text, kind = 'ref', className, style }: PillProps) {
  const w = pillWidth(text);
  return (
    <g className={['pill', `pill-${kind}`, className].filter(Boolean).join(' ')} style={style}>
      <rect x={x} y={y} width={w} height={PILL_H} rx={3} />
      {/* Centred rather than set 7px in from the left edge. `pillWidth` is
          the text plus 7px of padding on each side, so for a Latin ref this
          lands in exactly the same place — and for `غير قابل للوصول` it is
          the difference between a label inside its border and one pushed out
          of the side of it. */}
      <text x={x + w / 2} y={y + 11} textAnchor="middle">
        {text}
      </text>
    </g>
  );
}

type Dir = 'right' | 'left' | 'up' | 'down';

export interface HeadProps {
  /** The tip, so a head sits exactly where its line stops. */
  x: number;
  y: number;
  dir: Dir;
  className?: string;
  style?: CSSProperties;
  /** The resting opacity, which is also where a `fx-flash` beat ends. */
  opacity?: number;
}

/** An arrowhead, as a filled triangle pointing one of four ways. */
export function Head({ x, y, dir, className, style, opacity }: HeadProps) {
  const long = 6;
  const half = 3.6;
  const d =
    dir === 'right'
      ? `M${x} ${y} L${x - long} ${y - half} L${x - long} ${y + half} Z`
      : dir === 'left'
        ? `M${x} ${y} L${x + long} ${y - half} L${x + long} ${y + half} Z`
        : dir === 'up'
          ? `M${x} ${y} L${x - half} ${y + long} L${x + half} ${y + long} Z`
          : `M${x} ${y} L${x - half} ${y - long} L${x + half} ${y - long} Z`;
  return <path className={['head', className].filter(Boolean).join(' ')} d={d} style={style} opacity={opacity} />;
}
